import apiClient from './apiClient';

// ============================================================
// Types — matching backend Go models (FINBillData)
// ============================================================

export interface FINBillData {
  bill_mid: number;          // PK (autoIncrement)
  bill_type: string;         // AP=應付票據, AR=應收票據
  check_no: string;          // 支票號碼
  make_date: string | null;  // 開立日期 (ISO)
  expire_date: string | null; // 到期日期 (ISO)
  check_amt: number;         // 票面金額
  payee: string;             // 受款人
  check_bank: string;        // 出票銀行
  bill_sort: string;         // 票據類別 P=支票, R=收票
  summary: string;           // 摘要
  curr_code: string;         // 幣別
  rate: number;              // 匯率
  make_check: string;        // 開票人
  pay_bank: string;          // 付款銀行
  pay_bank_acc_no: string;   // 付款銀行帳號
  status: string;            // 票據狀態 O/A/P/Q/K/S/T
  rec_date: string | null;   // 收票日期 (ISO)
  rec_bank: string;          // 托收銀行
  rec_bank_acc_no: string;   // 托收銀行帳號
  company: string;           // 公司代號
  no_jb: boolean | null;     // 記帳否
  no_tt: boolean | null;     // 不含在途
  bill_no: string;           // 單據號碼
  out_date: string | null;   // 出票日期 (ISO)
  in_date: string | null;    // 入帳日期 (ISO)
  cash_date: string | null;  // 兌現日期 (ISO)
  cash_bank: string;         // 兌現銀行
  cash_bank_acc_no: string;  // 兌現銀行帳號
  cancel_date: string | null; // 作廢日期 (ISO)
  is_mis: boolean | null;    // 是否遺失
  c_user: string;            // 建立人
  c_date: string;            // 建立日期
  c_time: string;            // 建立時間
  u_user: string;            // 修改人
  u_date: string;            // 修改日期
}

export interface LookupItem {
  code: string;
  name: string;
}

// ============================================================
// Request / Response types
// ============================================================

export interface BillDataListResponse {
  data: FINBillData[];
  total: number;
  page: number;
  size: number;
}

export interface BillDataSearchParams {
  check_no?: string;
  status?: string;
  bill_type?: string;
  payee?: string;
  bill_no?: string;
  make_date_from?: string;
  make_date_to?: string;
  page?: number;
  size?: number;
}

export interface ValidationError {
  field: string;
  rule: string;
  message: string;
}

// ============================================================
// API methods
// ============================================================

export const finBillDataApi = {
  async list(page = 1, size = 20): Promise<BillDataListResponse> {
    const res = await apiClient.get('/api/fin-billdata', { params: { page, size } });
    return res.data;
  },

  async get(id: number): Promise<FINBillData> {
    const res = await apiClient.get(`/api/fin-billdata/${id}`);
    return res.data;
  },

  async search(params: BillDataSearchParams): Promise<BillDataListResponse> {
    const res = await apiClient.get('/api/fin-billdata/search', { params });
    return res.data;
  },

  async create(data: Partial<FINBillData>): Promise<FINBillData> {
    const res = await apiClient.post('/api/fin-billdata', data);
    return res.data;
  },

  async update(id: number, data: Partial<FINBillData>): Promise<FINBillData> {
    const res = await apiClient.put(`/api/fin-billdata/${id}`, data);
    return res.data;
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`/api/fin-billdata/${id}`);
  },

  // Lookup endpoints
  async lookupBanks(): Promise<LookupItem[]> {
    const res = await apiClient.get('/api/fin-billdata/lookup/banks');
    return res.data;
  },

  async lookupCurrencies(): Promise<LookupItem[]> {
    const res = await apiClient.get('/api/fin-billdata/lookup/currencies');
    return res.data;
  },

  async lookupCompanies(): Promise<LookupItem[]> {
    const res = await apiClient.get('/api/fin-billdata/lookup/companies');
    return res.data;
  },

  async lookupBillSorts(): Promise<LookupItem[]> {
    const res = await apiClient.get('/api/fin-billdata/lookup/bill-sorts');
    return res.data;
  },
};
